import { Inject, Injectable, InjectionToken, OnDestroy, Optional } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** The phone breakpoint: the prototype switches to its phone shell at `(max-width:760px)` (P/Member_Hub.html:489, :825). */
export const PHONE_MEDIA_QUERY = '(max-width: 760px)';

/** `window.matchMedia`'s shape, so a spec (or a host without a window) can supply its own. */
export type AGUMatchMedia = (query: string) => MediaQueryList;

/** The `matchMedia` the viewport service reads; defaults to the window's, and to null where there is none. */
export const AGU_MATCH_MEDIA = new InjectionToken<AGUMatchMedia | null>('AGUMatchMedia', {
    factory: () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? (query: string) => window.matchMedia(query) : null),
});

/**
 * Whether the surface is on a phone, live: `matchMedia(PHONE_MEDIA_QUERY)` with a 'change' listener, as the prototype's phone shell
 * does (P/Member_Hub.html:825-830).
 *
 * Provided by each surface component (like `AGUSurfaceReadyService`), never `providedIn: 'root'`, so the listener lives and dies with
 * the surface. The 'change' event arrives outside any Angular notification on this zoneless host: a consumer that paints from
 * `IsPhone$` asks `AGURenderService.Notify()` for the repaint.
 */
@Injectable()
export class AGUViewportService implements OnDestroy {
    private readonly query: MediaQueryList | null;
    private readonly isPhone: BehaviorSubject<boolean>;
    private readonly onChange = (event: MediaQueryListEvent): void => this.isPhone.next(event.matches);

    /** True while the viewport matches `PHONE_MEDIA_QUERY`; replays the current value to each new subscriber. */
    readonly IsPhone$: Observable<boolean>;

    constructor(@Optional() @Inject(AGU_MATCH_MEDIA) matchMedia: AGUMatchMedia | null) {
        this.query = matchMedia ? matchMedia(PHONE_MEDIA_QUERY) : null;
        this.isPhone = new BehaviorSubject<boolean>(this.query?.matches ?? false);
        this.IsPhone$ = this.isPhone.asObservable();
        this.query?.addEventListener('change', this.onChange);
    }

    get IsPhone(): boolean {
        return this.isPhone.value;
    }

    ngOnDestroy(): void {
        this.query?.removeEventListener('change', this.onChange);
        this.isPhone.complete();
    }
}
